import React from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { unpackToDateTime } from '@campfire/hot-date';
import sanitizeHtml from 'sanitize-html';

interface EmailContentProps {
  subject: string;
  from: string;
  to: string;
  sentAt: string | null;
  body: string | null;
  loading?: boolean;
}

const EMAIL_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'center', 'font']),
  allowedAttributes: {
    '*': ['style', 'align', 'valign', 'width', 'height', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'class'],
    a: ['href', 'name', 'target', 'rel'],
    img: ['src', 'alt', 'title', 'width', 'height'],
  },
};

const EmailMetaRow = ({ label, value }: { label: string; value: string }) => (
  <Box display='flex' style={{ gap: 8 }} marginBottom='4px'>
    <Typography style={{ fontSize: 13, color: '#646464', width: 56, flexShrink: 0 }}>{label}</Typography>
    <Typography style={{ fontSize: 13, color: '#444444', wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

/**
 * The single email view used by every communications surface: the profile's CommunicationDetailDialog
 * and the Communications Sent panel. SMS goes through SmsThread the same way.
 */
export const EmailContent = ({ subject, from, to, sentAt, body, loading }: EmailContentProps) => (
  <Box>
    <Typography style={{ fontSize: 16, fontWeight: 600, color: '#444444', marginBottom: 12 }}>{subject}</Typography>
    <EmailMetaRow label='From' value={from} />
    <EmailMetaRow label='To' value={to} />
    <EmailMetaRow label='Sent' value={sentAt ? unpackToDateTime(sentAt).toFormat('h:mm a, d LLLL yyyy') : 'Unknown'} />
    <Box
      marginTop='16px'
      padding='16px'
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EFEFEF',
        borderRadius: 4,
        fontSize: 14,
        lineHeight: 1.5,
        color: '#333333',
        overflowX: 'auto',
      }}
    >
      {loading ? (
        <Box display='flex' justifyContent='center'>
          <CircularProgress size={18} />
        </Box>
      ) : (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(body || 'No body content available', EMAIL_SANITIZE_OPTIONS),
          }}
        />
      )}
    </Box>
  </Box>
);
